import type { Catalogs, Translation } from "@amtana/i18n/catalog";

// The back office's words for organizations, people and staff (epic 09). `ops-copy.ts` puts
// them together with the rest into the one catalogue the screens read.

export type TenantCatalog = typeof EN;

const EN = {
  // Organizations (S9.03, S9.05).
  organizationsTitle: "Organizations",
  organizationsDescription:
    "Every organization on the platform. Sort, filter and step through them a page at a time.",
  searchOrganizations: "Search by name or slug",
  emptyOrganizations: "No organization matches.",
  columnOrganization: "Organization",
  columnPlan: "Plan",
  columnStatus: "Status",
  columnMembers: "Members",
  columnBalance: "Credits",
  columnCreated: "Created",
  planFree: "Free",
  planStarter: "Starter",
  planGrowth: "Growth",
  planEnterprise: "Enterprise",
  planUnlimited: "Unlimited",
  periodMonth: "Monthly",
  periodYear: "Yearly",
  unlimited: "Unlimited",
  organizationFacts: "Key facts",
  factPlan: "Plan",
  factBillingStatus: "Billing status",
  factBalance: "Credits left",
  factSeats: "Seats used",
  factCreated: "Created",
  factSlug: "Slug",
  factDeletion: "Deletion",
  deletionScheduled: "Scheduled, purged",
  membersTitle: "Members",
  emptyMembers: "No members.",
  columnPerson: "Person",
  columnRole: "Role",
  disabled: "Disabled",
  invoicesTitle: "Invoices",
  emptyInvoices: "No invoices yet.",
  columnInvoice: "Invoice",
  columnAmount: "Amount",
  columnDate: "Date",
  organizationGone: "This organization does not exist.",
  setSubscriptionAction: "Set subscription",
  setSubscriptionTitle: "Set subscription",
  setSubscriptionDescription:
    "Puts the organization on this plan directly, with these seats, optionally ending on a date and recording a payment taken outside Stripe. Always a manual subscription; the organization's admins see this change in their audit log.",
  setSubscriptionSubmit: "Set subscription",
  fieldNewPlan: "Plan",
  fieldBillingPeriod: "Billing period",
  fieldSeatsExtra: "Extra seats",
  fieldEndsAt: "Ends on",
  endsAtHint: "Leave empty for a subscription that never ends on its own.",
  recordExternalPayment: "Record a payment taken outside Stripe",
  fieldPaymentAmount: "Amount",
  paymentAmountHint: "As the customer paid it, like 500 or 49.90.",
  paymentAmountInvalid: "Enter an amount above zero, like 500 or 49.90.",
  fieldPaymentCurrency: "Currency",
  fieldPaymentReference: "Reference",
  paymentReferenceHint: 'e.g. "bank transfer 2026-10".',
  paymentReferenceMissing: "Enter a reference, so the invoice says where the money came from.",
  setSubscriptionReasonPlaceholder: "Upgrading per the sales agreement",
  changeFrom: "Now",
  changeTo: "After",
  grantCreditsAction: "Grant credits",
  grantCreditsTitle: "Grant credits",
  grantCreditsDescription:
    "Adds credits to the balance as a staff adjustment. The ledger and the organization's audit log keep the reason.",
  grantCreditsSubmit: "Grant credits",
  grantCreditsReasonPlaceholder: "Paid by bank transfer, invoice 2026-031",
  fieldAmount: "Credits",
  amountHint: "A whole number above zero.",
  amountInvalid: "Enter a whole number above zero.",
  linkStripeAction: "Link to Stripe",
  linkStripeTitle: "Link to Stripe",
  linkStripeDescription:
    "Validates the customer, and the subscription if one is given, with the payment provider before linking. From then on, Stripe's webhooks keep this organization's plan in step.",
  linkStripeSubmit: "Link",
  fieldStripeCustomerId: "Stripe customer id",
  fieldStripeSubscriptionId: "Stripe subscription id",
  stripeSubscriptionIdHint: "Optional. Leave empty to link only the customer.",
  linkStripeReasonPlaceholder: "Customer paid directly in the Stripe dashboard",
  detachStripeAction: "Detach from Stripe",
  detachStripeTitle: "Detach from Stripe",
  detachStripeDescription:
    "Clears the Stripe link. Choose whether the plan stays exactly as it is, as a manual subscription, or drops to Free.",
  detachStripeSubmit: "Detach",
  fieldKeepAs: "After detaching",
  keepAsManual: "Keep the plan, as manual",
  keepAsFree: "Drop to Free",
  detachStripeReasonPlaceholder: "Sandbox organization, cleaning up before going live",
  toastSubscriptionSet: "Subscription set",
  toastCreditsGranted: "Credits granted",
  toastLinkedToStripe: "Linked to Stripe",
  toastDetachedFromStripe: "Detached from Stripe",
  errorSubscriptionTitle: "The subscription did not change",
  errorCreditsTitle: "No credits were granted",
  errorLinkStripeTitle: "Could not link to Stripe",
  errorDetachStripeTitle: "Could not detach from Stripe",
  factSeatsIncluded: "Seats included",
  factSeatsExtra: "Extra seats",
  factSource: "Source",
  sourceStripe: "Stripe",
  sourceManual: "Manual",
  factRenewsAt: "Renews",
  factEndsAt: "Ends",
  factStripeCustomerId: "Stripe customer",
  factStripeSubscriptionId: "Stripe subscription",
  billingActive: "Active",
  billingNoSubscription: "No subscription",
  billingPastDue: "Past due",
  billingFrozen: "Frozen",
  billingCancelling: "Cancelling",
  billingDisputed: "Disputed",
  roleAdmin: "Admin",
  roleMember: "Member",
  roleViewer: "Viewer",
  invoicePaid: "Paid",
  invoiceRefunded: "Refunded",
  invoiceExternal: "External",
  invoiceBitcoin: "Bitcoin, {sats} sats",

  // Users (S9.04-S9.06).
  peopleTitle: "Users",
  peopleDescription:
    "Everyone with an account. Search by name or email, and filter by when they joined or were last seen.",
  searchPeople: "Search by name or email",
  emptyPeople: "No one matches.",
  personFacts: "Key facts",
  factEmail: "Email",
  factVerified: "Address verified",
  factSignIn: "Sign-in methods",
  factLastSeen: "Last seen",
  factTerms: "Terms accepted",
  termsNotAccepted: "Not yet",
  termsCurrent: "In force",
  termsOutdated: "Older version",
  termsAcceptedLine: "Version {version}, {date}",
  signInPassword: "Password",
  signInPasskeys: { one: "1 passkey", other: "{count} passkeys" },
  signInSso: "Single sign-on",
  membershipsTitle: "Memberships",
  emptyMemberships: "Not a member of any organization.",
  sessionsTitle: "Sessions",
  emptySessions: "No active sessions.",
  columnDevice: "Device",
  columnLastSeen: "Last seen",
  columnStarted: "Started",
  personGone: "This person does not exist.",
  personErasedLabel: "Erased",
  personErasedName: "Erased person",
  endSessionsAction: "End sessions",
  endSessionsTitle: "End every session",
  endSessionsDescription: "Signs this person out everywhere; they sign in again to continue.",
  endSessionsSubmit: "End sessions",
  toastSessionsEnded: "Sessions ended",
  errorSessionsTitle: "The sessions did not end",
  viewAsAction: "View as",
  viewAsTitle: "View as this person",
  viewAsDescription:
    "You use the platform as they do, with their own role, for up to 30 minutes. You can change what they can, except their password, email, passkeys, sessions and API keys. A banner shows the whole time, and every change is recorded as yours, made for them.",
  viewAsSubmit: "Start viewing",
  errorViewAsTitle: "The visit did not start",
  viewAsStaffTooltip: "Staff are never viewed as: this person holds a staff grant.",
  visitReasonMissing: "Say why, in a word or more: the visit starts only with a reason.",
  errorCannotImpersonateStaff: "This person holds a staff grant; staff cannot view as staff.",
  errorNoOrganization: "This person belongs to no organization, so there is nothing to view.",

  // Erasing a person (ADR-026 point 1, epic 30, S30.03).
  eraseAction: "Erase person",
  eraseTitle: "Erase this person",
  eraseDescription:
    "Permanently erases this person across every module (GDPR). Runs immediately, with no grace period; a reason and typing their email are both required.",
  eraseSubmit: "Erase person",
  fieldConfirmEmail: "Type their email to confirm",
  erasureReasonPlaceholder: "Why this person is being erased",
  previewSharedAdminTitle: "Organizations they co-administer (stay, their membership is removed)",
  previewSoleTitle: "Organizations that go with them (they are the only member)",
  toastPersonErased: "Person erased",
  errorEraseTitle: "The person was not erased",
  errorErasureConfirmMismatch: "That email doesn't match the person's own.",
  errorErasureStaffTarget: "That person holds a staff grant; revoke it first, then erase them.",
  errorErasureSelf: "You cannot erase yourself through this door.",
  erasureProgressTitle: "Erasure progress",
  columnModule: "Module",
  moduleStatusPending: "Pending",
  moduleStatusErased: "Erased",
  moduleStatusFailed: "Failed",
  emptyErasureProgress: "This person has not been erased.",

  // Staff (S9.10).
  staffTitle: "Staff",
  staffDescription: "Who may open the back office. Read sees it; manage also changes things.",
  emptyStaff: "No staff grants.",
  columnLevel: "Level",
  columnGranted: "Granted",
  levelRead: "Read",
  levelManage: "Manage",
  levelReadHint: "Sees every page of the back office.",
  levelManageHint: "Also changes plans, credits, flags, staff and queues.",
  grantStaffAction: "Grant staff",
  grantStaffTitle: "Grant staff",
  grantStaffDescription:
    "Gives this person the back office at the level you choose. A grant they already hold changes to the new level.",
  grantStaffSubmit: "Grant",
  fieldPerson: "Person",
  fieldLevel: "Level",
  pickPerson: "Choose a person first.",
  changePerson: "Change",
  revokeAction: "Revoke",
  revokeTitle: "Revoke this grant",
  revokeDescription: "This person loses the back office at once. The platform always keeps one manager.",
  revokeSubmit: "Revoke",
  toastGranted: "Staff granted",
  toastRevoked: "Grant revoked",
  errorStaffTitle: "Staff did not change",
  errorLastManagerDescription: "Grant manage to someone else before revoking or lowering the last manager.",
} as const;

const ES: Translation<TenantCatalog> = {
  // Organizations (S9.03, S9.05).
  organizationsTitle: "Organizaciones",
  organizationsDescription:
    "Todas las organizaciones de la plataforma. Ordénalas, fíltralas y recórrelas página a página.",
  searchOrganizations: "Busca por nombre o slug",
  emptyOrganizations: "Ninguna organización coincide.",
  columnOrganization: "Organización",
  columnPlan: "Plan",
  columnStatus: "Estado",
  columnMembers: "Miembros",
  columnBalance: "Créditos",
  columnCreated: "Creada",
  planFree: "Gratis",
  planStarter: "Starter",
  planGrowth: "Growth",
  planEnterprise: "Enterprise",
  planUnlimited: "Unlimited",
  periodMonth: "Mensual",
  periodYear: "Anual",
  unlimited: "Unlimited",
  organizationFacts: "Datos clave",
  factPlan: "Plan",
  factBillingStatus: "Estado de facturación",
  factBalance: "Créditos restantes",
  factSeats: "Asientos en uso",
  factCreated: "Creada",
  factSlug: "Slug",
  factDeletion: "Eliminación",
  deletionScheduled: "Programada, se purga el",
  membersTitle: "Miembros",
  emptyMembers: "Sin miembros.",
  columnPerson: "Persona",
  columnRole: "Rol",
  disabled: "Desactivado",
  invoicesTitle: "Facturas",
  emptyInvoices: "Aún no hay facturas.",
  columnInvoice: "Factura",
  columnAmount: "Importe",
  columnDate: "Fecha",
  organizationGone: "Esta organización no existe.",
  setSubscriptionAction: "Establecer suscripción",
  setSubscriptionTitle: "Establecer la suscripción",
  setSubscriptionDescription:
    "Pone a la organización directamente en este plan, con estos asientos, terminando opcionalmente en una fecha y registrando un pago recibido fuera de Stripe. Siempre queda como una suscripción manual; los administradores de la organización ven este cambio en su registro de auditoría.",
  setSubscriptionSubmit: "Establecer suscripción",
  fieldNewPlan: "Plan",
  fieldBillingPeriod: "Periodo de facturación",
  fieldSeatsExtra: "Asientos adicionales",
  fieldEndsAt: "Termina el",
  endsAtHint: "Déjalo vacío para una suscripción que nunca termina por sí sola.",
  recordExternalPayment: "Registrar un pago recibido fuera de Stripe",
  fieldPaymentAmount: "Importe",
  paymentAmountHint: "Tal como pagó el cliente, por ejemplo 500 o 49,90.",
  paymentAmountInvalid: "Escribe un importe mayor que cero, por ejemplo 500 o 49,90.",
  fieldPaymentCurrency: "Moneda",
  fieldPaymentReference: "Referencia",
  paymentReferenceHint: 'Por ejemplo, "transferencia bancaria 2026-10".',
  paymentReferenceMissing: "Escribe una referencia, para que la factura diga de dónde vino el dinero.",
  setSubscriptionReasonPlaceholder: "Mejora según el acuerdo de venta",
  changeFrom: "Ahora",
  changeTo: "Después",
  grantCreditsAction: "Otorgar créditos",
  grantCreditsTitle: "Otorgar créditos",
  grantCreditsDescription:
    "Suma créditos al saldo como un ajuste del staff. El libro de créditos y el registro de auditoría de la organización guardan el motivo.",
  grantCreditsSubmit: "Otorgar créditos",
  grantCreditsReasonPlaceholder: "Pagado por transferencia bancaria, factura 2026-031",
  fieldAmount: "Créditos",
  amountHint: "Un número entero mayor que cero.",
  amountInvalid: "Escribe un número entero mayor que cero.",
  linkStripeAction: "Vincular a Stripe",
  linkStripeTitle: "Vincular a Stripe",
  linkStripeDescription:
    "Valida el cliente, y la suscripción si se da una, con el proveedor de pagos antes de vincular. A partir de ahí, los webhooks de Stripe mantienen al día el plan de esta organización.",
  linkStripeSubmit: "Vincular",
  fieldStripeCustomerId: "Id de cliente de Stripe",
  fieldStripeSubscriptionId: "Id de suscripción de Stripe",
  stripeSubscriptionIdHint: "Opcional. Déjalo vacío para vincular solo el cliente.",
  linkStripeReasonPlaceholder: "El cliente pagó directamente en el panel de Stripe",
  detachStripeAction: "Desvincular de Stripe",
  detachStripeTitle: "Desvincular de Stripe",
  detachStripeDescription:
    "Borra el vínculo con Stripe. Elige si el plan se mantiene tal cual, como suscripción manual, o baja a Free.",
  detachStripeSubmit: "Desvincular",
  fieldKeepAs: "Después de desvincular",
  keepAsManual: "Mantener el plan, como manual",
  keepAsFree: "Bajar a Free",
  detachStripeReasonPlaceholder: "Organización de prueba, limpiando antes de salir a producción",
  toastSubscriptionSet: "Suscripción establecida",
  toastCreditsGranted: "Créditos otorgados",
  toastLinkedToStripe: "Vinculado a Stripe",
  toastDetachedFromStripe: "Desvinculado de Stripe",
  errorSubscriptionTitle: "La suscripción no cambió",
  errorCreditsTitle: "No se otorgaron créditos",
  errorLinkStripeTitle: "No se pudo vincular a Stripe",
  errorDetachStripeTitle: "No se pudo desvincular de Stripe",
  factSeatsIncluded: "Asientos incluidos",
  factSeatsExtra: "Asientos adicionales",
  factSource: "Origen",
  sourceStripe: "Stripe",
  sourceManual: "Manual",
  factRenewsAt: "Renueva",
  factEndsAt: "Termina",
  factStripeCustomerId: "Cliente de Stripe",
  factStripeSubscriptionId: "Suscripción de Stripe",
  billingActive: "Activa",
  billingNoSubscription: "Sin suscripción",
  billingPastDue: "Pago vencido",
  billingFrozen: "Congelada",
  billingCancelling: "Cancelándose",
  billingDisputed: "En disputa",
  roleAdmin: "Administrador",
  roleMember: "Miembro",
  roleViewer: "Lector",
  invoicePaid: "Pagada",
  invoiceRefunded: "Reembolsada",
  invoiceExternal: "Externo",
  invoiceBitcoin: "Bitcoin, {sats} sats",

  // Users (S9.04-S9.06).
  peopleTitle: "Usuarios",
  peopleDescription:
    "Todas las personas con una cuenta. Busca por nombre o correo, y filtra por alta o última actividad.",
  searchPeople: "Busca por nombre o correo",
  emptyPeople: "Nadie coincide.",
  personFacts: "Datos clave",
  factEmail: "Correo",
  factVerified: "Dirección verificada",
  factSignIn: "Formas de iniciar sesión",
  factLastSeen: "Visto por última vez",
  factTerms: "Términos aceptados",
  termsNotAccepted: "Todavía no",
  termsCurrent: "Vigentes",
  termsOutdated: "Versión anterior",
  termsAcceptedLine: "Versión {version}, {date}",
  signInPassword: "Contraseña",
  signInPasskeys: { one: "1 passkey", other: "{count} passkeys" },
  signInSso: "Inicio de sesión único",
  membershipsTitle: "Membresías",
  emptyMemberships: "No es miembro de ninguna organización.",
  sessionsTitle: "Sesiones",
  emptySessions: "Sin sesiones activas.",
  columnDevice: "Dispositivo",
  columnLastSeen: "Visto por última vez",
  columnStarted: "Inicio",
  personGone: "Esta persona no existe.",
  personErasedLabel: "Borrada",
  personErasedName: "Persona borrada",
  endSessionsAction: "Cerrar sesiones",
  endSessionsTitle: "Cerrar todas las sesiones",
  endSessionsDescription:
    "Cierra la sesión de esta persona en todas partes. Tendrá que iniciar sesión de nuevo.",
  endSessionsSubmit: "Cerrar sesiones",
  toastSessionsEnded: "Sesiones cerradas",
  errorSessionsTitle: "Las sesiones no se cerraron",
  viewAsAction: "Ver como",
  viewAsTitle: "Ver como esta persona",
  viewAsDescription:
    "Usas la plataforma como ella, con su propio rol, hasta 30 minutos. Puedes cambiar lo que ella puede, salvo su contraseña, correo, llaves de acceso, sesiones y claves de API. Un aviso se muestra todo el tiempo y cada cambio queda registrado como tuyo, hecho en su nombre.",
  viewAsSubmit: "Empezar a ver",
  errorViewAsTitle: "La visita no empezó",
  viewAsStaffTooltip: "Al staff no se le ve como a otra persona: esta persona tiene un permiso de staff.",
  visitReasonMissing: "Di por qué, con una palabra o más: la visita empieza solo con un motivo.",
  errorCannotImpersonateStaff: "Esta persona tiene un permiso de staff. No se puede ver como staff.",
  errorNoOrganization: "Esta persona no pertenece a ninguna organización, así que no hay nada que ver.",

  // Borrar a una persona (ADR-026 punto 1, épica 27, S30.03).
  eraseAction: "Borrar persona",
  eraseTitle: "Borrar a esta persona",
  eraseDescription:
    "Borra permanentemente a esta persona en cada módulo (GDPR). Corre de inmediato, sin periodo de gracia; el motivo y escribir su correo son obligatorios.",
  eraseSubmit: "Borrar persona",
  fieldConfirmEmail: "Escribe su correo para confirmar",
  erasureReasonPlaceholder: "Por qué se borra a esta persona",
  previewSharedAdminTitle: "Organizaciones que co-administra (quedan, se elimina su membresía)",
  previewSoleTitle: "Organizaciones que se van con ella (es la única integrante)",
  toastPersonErased: "Persona borrada",
  errorEraseTitle: "La persona no se borró",
  errorErasureConfirmMismatch: "Ese correo no coincide con el de la persona.",
  errorErasureStaffTarget: "Esa persona tiene un permiso de staff; revócalo primero y luego bórrala.",
  errorErasureSelf: "No puedes borrarte a ti mismo por esta puerta.",
  erasureProgressTitle: "Progreso del borrado",
  columnModule: "Módulo",
  moduleStatusPending: "Pendiente",
  moduleStatusErased: "Borrado",
  moduleStatusFailed: "Falló",
  emptyErasureProgress: "Esta persona no ha sido borrada.",

  // Staff (S9.10).
  staffTitle: "Staff",
  staffDescription: "Quién puede abrir el back office. Lectura lo ve; gestión también cambia cosas.",
  emptyStaff: "Sin permisos de staff.",
  columnLevel: "Nivel",
  columnGranted: "Otorgado",
  levelRead: "Lectura",
  levelManage: "Gestión",
  levelReadHint: "Ve todas las páginas del back office.",
  levelManageHint: "También cambia planes, créditos, flags, staff y colas.",
  grantStaffAction: "Dar acceso al staff",
  grantStaffTitle: "Dar acceso al staff",
  grantStaffDescription:
    "Da a esta persona el back office en el nivel que elijas. Si ya tiene un permiso, pasa al nuevo nivel.",
  grantStaffSubmit: "Otorgar",
  fieldPerson: "Persona",
  fieldLevel: "Nivel",
  pickPerson: "Elige primero a una persona.",
  changePerson: "Cambiar",
  revokeAction: "Revocar",
  revokeTitle: "Revocar este permiso",
  revokeDescription:
    "Esta persona pierde el back office de inmediato. La plataforma siempre conserva un gestor.",
  revokeSubmit: "Revocar",
  toastGranted: "Acceso de staff otorgado",
  toastRevoked: "Permiso revocado",
  errorStaffTitle: "El staff no cambió",
  errorLastManagerDescription: "Da gestión a otra persona antes de revocar o bajar al último gestor.",
};

export const TENANT_CATALOGS: Catalogs<TenantCatalog> = { en: EN, es: ES };
